import { FC } from 'react';
import { Card, Flex, Box, StarRating, Heading, Text, Link as RooLink } from '@qga/roo-ui/components';

import { HiddenOnSmallScreen } from 'components/Layout';
import PropertyImage from 'components/PropertyImage';
import { Link } from 'components/Typography';
import PropertyInformationModal from 'components/Modals/PropertyInformationModal';

import GetDirectionsLinksWithTracking from './GetDirectionsLinksWithTracking';
import HorizontalRuleMobile from './HorizontalRuleMobile';
import { SelfServiceAPI } from 'types';
import useRoutableModal from 'hooks/useRoutableModal';
import * as track from 'lib/gaTracking/bookingDetailsPageEvents';
import Links from '../Links';

interface HotelDetailsCardProps {
  booking: SelfServiceAPI.Booking;
}

const HotelDetailsCard: FC<HotelDetailsCardProps> = ({ booking }) => {
  const { RoutableModalWrapper, ...modal } = useRoutableModal('property-information', {
    onCloseIteraction: track.showHotelPopUpCloseButtonSelected,
  });

  const { property } = booking.reservation;
  const rawRating = Number(property.rating);
  const rating = Number.isNaN(rawRating) ? undefined : rawRating;
  const ratingType = property.ratingType === 'AAA' || property.ratingType === 'SELF_RATED' ? property.ratingType : null;

  return (
    <Card p={0} mb={[5, 8]} display="flex" overflow="hidden">
      <Flex flexDirection={['column', 'row']} width="100%">
        <HiddenOnSmallScreen>
          <Box width="33.33%">
            <PropertyImage {...booking.largeImage} ratio="73%" height="100%" />
          </Box>
        </HiddenOnSmallScreen>
        <Box p={5} width={['100%', '50%']}>
          <Heading.h4 m={0} mb={0} fontWeight="normal" fontSize="xl">
            {property.name}
          </Heading.h4>
          <Box mt={1}>
            <StarRating rating={rating} ratingType={ratingType} size={16} />
          </Box>
          <Box mt={3}>
            <Link to={modal.path} onClick={track.showHotelPopUpLinkSelected}>
              Show hotel information
            </Link>
          </Box>
          <Box mt={3}>
            <Text color="greys.steel">Address</Text>
          </Box>
          <Box>
            <Text>{property.formattedAddress}</Text>
          </Box>
          <Box mt={2}>
            <GetDirectionsLinksWithTracking booking={booking} />
          </Box>
          {property.phone && (
            <>
              <Box mt={3}>
                <Text color="greys.steel">Phone</Text>
              </Box>
              <Box>
                <RooLink href={`tel: ${property.phone}`} color="greys.charcoal">
                  {property.phone}
                </RooLink>
              </Box>
            </>
          )}
          {property.email && (
            <>
              <Box mt={3}>
                <Text color="greys.steel">Email</Text>
              </Box>
              <Box>
                <RooLink href={`mailto: ${property.email}`} color="greys.charcoal">
                  {property.email}
                </RooLink>
              </Box>
            </>
          )}
        </Box>
        <HorizontalRuleMobile mx={5} my={0} />
        <Box p={5} flexDirection={['column', 'row']} borderLeft={[0, 1]} borderColor={['', 'greys.alto']}>
          <Links booking={booking} />
        </Box>
      </Flex>

      <RoutableModalWrapper>
        <PropertyInformationModal booking={booking} {...modal.props} />
      </RoutableModalWrapper>
    </Card>
  );
};

export default HotelDetailsCard;
